import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail, fileUrl, downloadBlob } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, StatusBadge, assetTypeLabel, EmptyState, trackingUnit, DeadlineBadges } from "@/components/shared";
import { PhotoUpload } from "@/components/PhotoUpload";
import MultiFilter from "@/components/MultiFilter";
import { Plus, Search, Truck, Pencil, Trash2, FileDown, Package, ChevronsUpDown, ChevronUp, ChevronDown, Loader2 } from "lucide-react";

const empty = {
  name: "", category_id: "", asset_type: "mezzo", management_type: "seriale", quantity: 1, tracking_type: "km",
  identifier: "", brand: "", model: "", year: "", status: "disponibile",
  current_value: 0, notes: "",
};

export default function Assets() {
  const [assets, setAssets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [assetTypes, setAssetTypes] = useState([]);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState({ key: "name", dir: "asc" });
  const [filters, setFilters] = useState({ name: [], category_name: [], asset_type: [], identifier: [], current_value: [], deadlines: [], assigned_to: [], status: [] });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const load = () => {
    setLoading(true);
    api.get("/assets").then(({ data }) => setAssets(data)).finally(() => setLoading(false));
    api.get("/categories").then(({ data }) => setCategories(data));
  };
  useEffect(load, []);

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (a) => {
    setForm({ ...empty, ...a });
    setEditId(a.id);
    setOpen(true);
  };

  const save = async () => {
    if (!form.name || !form.category_id) {
      toast.error("Nome e categoria sono obbligatori");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, current_value: Number(form.current_value) || 0, quantity: Math.max(parseInt(form.quantity, 10) || 0, 0) };
      if (editId) await api.put(`/assets/${editId}`, payload);
      else await api.post("/assets", payload);
      toast.success(editId ? "Bene aggiornato" : "Bene censito");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Eliminare "${a.name}"? Verranno rimossi anche manutenzioni e assegnazioni.`)) return;
    try {
      await api.delete(`/assets/${a.id}`);
      toast.success("Bene eliminato");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const opt = (key, mapper = (x) => x) => [...new Set(assets.map(mapper).filter((v) => v !== null && v !== undefined && v !== ""))].sort();
  const reading = (a) => a.tracking_type !== "nessuno" ? `${a.current_value || 0} ${trackingUnit(a.tracking_type)}` : "—";
  const deadlineState = (a) => {
    const d = a.deadlines || [];
    if (!d.length) return "Nessuna";
    if (d.some((x) => x.status === "scaduta")) return "Scaduta";
    if (d.some((x) => x.status === "in_scadenza")) return "In scadenza";
    return "In regola";
  };
  const nameOptions = opt("name", (a) => a.name);
  const catOptions = opt("category_name", (a) => a.category_name);
  const typeOptions = assetTypes.map((t) => t.name);
  const identifierOptions = opt("identifier", (a) => a.identifier || "—");
  const readingOptions = [...new Set(assets.map(reading))].sort();
  const deadlineOptions = ["Scaduta", "In scadenza", "In regola", "Nessuna"];
  const assigneeOptions = opt("assigned_to", (a) => a.assigned_to || "Non assegnato");
  const statusOptions = ["disponibile", "assegnato", "manutenzione", "dismesso"];

  const matchFilter = (arr, val) => arr.length === 0 || arr.includes(val);

  const sortVal = (a) => {
    switch (sort.key) {
      case "name": return a.name?.toLowerCase() || "";
      case "category_name": return a.category_name?.toLowerCase() || "";
      case "asset_type": return a.asset_type || "";
      case "identifier": return a.identifier?.toLowerCase() || "";
      case "current_value": return a.current_value || 0;
      case "deadlines": return a.deadlines?.length ? a.deadlines[0].days : 99999;
      case "assigned_to": return a.assigned_to?.toLowerCase() || "";
      case "status": return a.status || "";
      default: return "";
    }
  };

  const filtered = assets
    .filter((a) => {
      const matchQ = [a.name, a.identifier, a.brand, a.model, a.category_name].join(" ").toLowerCase().includes(q.toLowerCase());
      return matchQ
        && matchFilter(filters.name, a.name)
        && matchFilter(filters.category_name, a.category_name)
        && matchFilter(filters.asset_type, a.asset_type)
        && matchFilter(filters.identifier, a.identifier || "—")
        && matchFilter(filters.current_value, reading(a))
        && matchFilter(filters.deadlines, deadlineState(a))
        && matchFilter(filters.assigned_to, a.assigned_to || "Non assegnato")
        && matchFilter(filters.status, a.status);
    })
    .sort((x, y) => {
      const vx = sortVal(x), vy = sortVal(y);
      const cmp = vx < vy ? -1 : vx > vy ? 1 : 0;
      return sort.dir === "asc" ? cmp : -cmp;
    });

  const toggleSort = (key) => setSort((s) => ({ key, dir: s.key === key && s.dir === "asc" ? "desc" : "asc" }));
  const SortIcon = ({ k }) => sort.key !== k ? <ChevronsUpDown className="h-3.5 w-3.5 opacity-40" /> : sort.dir === "asc" ? <ChevronUp className="h-3.5 w-3.5 text-primary" /> : <ChevronDown className="h-3.5 w-3.5 text-primary" />;
  const Th = ({ k, children, filter, className = "" }) => (
    <TableHead className={`label-caps ${className}`}>
      <div className="flex items-center justify-center gap-1">
        <button className="flex items-center gap-1 hover:text-primary" onClick={() => toggleSort(k)} data-testid={`sort-${k}`}>
          {children} <SortIcon k={k} />
        </button>
        {filter}
      </div>
    </TableHead>
  );

  return (
    <div>
      <PageHeader eyebrow="Censimento" title="Beni & Mezzi" subtitle="Anagrafica di mezzi, attrezzature e dispositivi">
        <Button variant="outline" onClick={() => downloadBlob("/export/assets", "beni.xlsx")} data-testid="export-assets-button" className="font-semibold">
          <FileDown className="h-4 w-4 mr-1.5" /> Esporta XLSX
        </Button>
        {isAdmin && (
          <Button onClick={openNew} data-testid="new-asset-button" className="font-semibold">
            <Plus className="h-4 w-4 mr-1.5" /> Nuovo bene
          </Button>
        )}
      </PageHeader>

      <div className="mb-4 relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input data-testid="asset-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per nome, targa, marca…" className="pl-9" />
      </div>

      <Card className="rounded-lg overflow-hidden">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 text-muted-foreground" data-testid="assets-loading">
            <Loader2 className="h-8 w-8 animate-spin mb-3 text-primary" />
            <span className="text-sm">Caricamento beni…</span>
          </div>
        ) : filtered.length ? (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-secondary/60 hover:bg-secondary/60">
                  <Th k="name"
                    filter={<MultiFilter label="Bene" options={nameOptions} value={filters.name} onChange={(v) => setFilters({ ...filters, name: v })} testid="filter-name" />}>Bene</Th>
                  <Th k="category_name" className="hidden md:table-cell"
                    filter={<MultiFilter label="Categoria" options={catOptions} value={filters.category_name} onChange={(v) => setFilters({ ...filters, category_name: v })} testid="filter-category" />}>Categoria</Th>
                  <Th k="asset_type" className="hidden sm:table-cell"
                    filter={<MultiFilter label="Tipo" options={typeOptions} value={filters.asset_type} onChange={(v) => setFilters({ ...filters, asset_type: v })} testid="filter-type" />}>Tipo</Th>
                  <Th k="identifier" className="hidden lg:table-cell"
                    filter={<MultiFilter label="Matricola/Targa" options={identifierOptions} value={filters.identifier} onChange={(v) => setFilters({ ...filters, identifier: v })} testid="filter-identifier" />}>Matricola/Targa</Th>
                  <Th k="current_value" className="hidden lg:table-cell"
                    filter={<MultiFilter label="Lettura" options={readingOptions} value={filters.current_value} onChange={(v) => setFilters({ ...filters, current_value: v })} testid="filter-reading" />}>Lettura</Th>
                  <Th k="deadlines"
                    filter={<MultiFilter label="Scadenze" options={deadlineOptions} value={filters.deadlines} onChange={(v) => setFilters({ ...filters, deadlines: v })} testid="filter-deadlines" />}>Prossime scadenze</Th>
                  <Th k="assigned_to"
                    filter={<MultiFilter label="Assegnatario" options={assigneeOptions} value={filters.assigned_to} onChange={(v) => setFilters({ ...filters, assigned_to: v })} testid="filter-assignee" />}>Assegnatario</Th>
                  <Th k="status"
                    filter={<MultiFilter label="Stato" options={statusOptions} value={filters.status} onChange={(v) => setFilters({ ...filters, status: v })} testid="filter-status" />}>Stato</Th>
                  {isAdmin && <TableHead className="label-caps">Azioni</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((a) => (
                  <TableRow key={a.id} data-testid={`asset-row-${a.id}`} className="cursor-pointer" onClick={() => navigate(`/beni/${a.id}`)}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-md border border-border bg-secondary/50 overflow-hidden shrink-0 flex items-center justify-center">
                          {a.image ? (
                            <img src={fileUrl(a.image)} alt={a.name} className="h-full w-full object-cover" />
                          ) : (
                            <Package className="h-4 w-4 text-muted-foreground/40" />
                          )}
                        </div>
                        <div>
                          <div className="font-semibold">{a.name}</div>
                          <div className="text-xs text-muted-foreground">{a.brand} {a.model}</div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell text-muted-foreground text-center">{a.category_name}</TableCell>
                    <TableCell className="hidden sm:table-cell text-center">{assetTypeLabel(a.asset_type)}</TableCell>
                    <TableCell className="hidden lg:table-cell font-mono text-sm text-center">{a.identifier || "—"}</TableCell>
                    <TableCell className="hidden lg:table-cell text-center tabular-nums">
                      {a.tracking_type !== "nessuno" ? `${a.current_value || 0} ${trackingUnit(a.tracking_type)}` : "—"}
                    </TableCell>
                    <TableCell><DeadlineBadges deadlines={a.deadlines} /></TableCell>
                    <TableCell className="text-center">{a.assigned_to || <span className="text-muted-foreground">—</span>}</TableCell>
                    <TableCell className="text-center">
                      {a.management_type === "quantita"
                        ? <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-md bg-primary/10 text-primary" data-testid={`asset-qty-badge-${a.id}`}>Disp. {a.available_qty}/{a.quantity}</span>
                        : <StatusBadge status={a.status} />}
                    </TableCell>
                    {isAdmin && (
                      <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="icon" data-testid={`edit-asset-${a.id}`} onClick={() => openEdit(a)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" data-testid={`delete-asset-${a.id}`} onClick={() => remove(a)}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <EmptyState icon={Truck} title="Nessun bene trovato" subtitle="Inizia censendo un mezzo, un'attrezzatura o un dispositivo."
            action={isAdmin ? <Button onClick={openNew}><Plus className="h-4 w-4 mr-1.5" /> Nuovo bene</Button> : null} />
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">{editId ? "Modifica bene" : "Nuovo bene"}</DialogTitle>
          </DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4 py-2">
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Foto del bene</Label>
              <PhotoUpload value={form.image} onChange={(path) => setForm({ ...form, image: path })} />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Denominazione *</Label>
              <Input data-testid="asset-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Es. Fiat Ducato / Trapano Bosch" />
            </div>
            <div className="space-y-1.5">
              <Label>Categoria *</Label>
              <Select value={form.category_id} onValueChange={(v) => setForm({ ...form, category_id: v })}>
                <SelectTrigger data-testid="asset-category-select"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                <SelectContent>
                  {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Tipologia</Label>
              <Select value={form.asset_type} onValueChange={(v) => setForm({ ...form, asset_type: v })}>
                <SelectTrigger data-testid="asset-type-select"><SelectValue placeholder="Seleziona tipologia" /></SelectTrigger>
                <SelectContent>
                  {assetTypes.map((t) => <SelectItem key={t.id} value={t.name} className="capitalize">{assetTypeLabel(t.name)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Gestione</Label>
              <Select value={form.management_type} onValueChange={(v) => setForm({ ...form, management_type: v })}>
                <SelectTrigger data-testid="asset-management-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="seriale">Per seriale — 1 tecnico</SelectItem>
                  <SelectItem value="quantita">Per quantità — più tecnici</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">"Per quantità" (es. martelli) può essere assegnato a più tecnici fino a esaurimento giacenza.</p>
            </div>
            {form.management_type === "quantita" && (
              <div className="space-y-1.5">
                <Label>Quantità totale (giacenza)</Label>
                <Input type="number" min="0" data-testid="asset-quantity-input" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} />
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Tracciamento uso</Label>
              <Select value={form.tracking_type} onValueChange={(v) => setForm({ ...form, tracking_type: v })}>
                <SelectTrigger data-testid="asset-tracking-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="km">Chilometri</SelectItem>
                  <SelectItem value="ore">Ore</SelectItem>
                  <SelectItem value="nessuno">Nessuno</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Lettura attuale ({trackingUnit(form.tracking_type) || "n/d"})</Label>
              <Input type="number" data-testid="asset-value-input" value={form.current_value} onChange={(e) => setForm({ ...form, current_value: e.target.value })} disabled={form.tracking_type === "nessuno"} />
            </div>
            <div className="space-y-1.5">
              <Label>Matricola / Targa</Label>
              <Input data-testid="asset-identifier-input" value={form.identifier} onChange={(e) => setForm({ ...form, identifier: e.target.value })} placeholder="Es. AB123CD" />
            </div>
            <div className="space-y-1.5">
              <Label>Marca</Label>
              <Input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Modello</Label>
              <Input value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Anno</Label>
              <Input value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} placeholder="2023" />
            </div>
            <div className="space-y-1.5">
              <Label>Stato</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="asset-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="disponibile">Disponibile</SelectItem>
                  <SelectItem value="manutenzione">In manutenzione</SelectItem>
                  <SelectItem value="dismesso">Dismesso</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Note</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={save} disabled={saving} data-testid="save-asset-button">{saving ? "Salvataggio…" : "Salva"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
